import { useQuery } from '@tanstack/react-query'
import { API_ENDPOINTS } from 'config/api'
import { HIDE_USD_BALANCE_THRESHOLD } from 'config/portfolio'
import { PORTFOLIO_REFETCH_POLICY } from 'config/RefetchPolicies'
import { enrichTokenWithData, useTokenData } from 'contexts/TokenDataContext'
import { transformAPIFarm } from 'hooks/backend/farms'
import { useConnection } from 'hooks/useConnection'
import { useReadContractMultiple } from 'hooks/useReadContractMultiple'
import { apiClient } from 'lib/api-client'
import { useMemo } from 'react'
import { APIFarm } from 'types/farms'
import { getFarmPositionTotalValue } from 'utils/portfolio/getFarmPositionTotalValue'
import { type Address, Abi, formatUnits, getAddress } from 'viem'

import ERC20_ABI from '../../abis/erc20.json'
import ISLAND_ABI from '../../abis/island.json'
import KodiakFarmAbi from '../../abis/KodiakFarm.json'
import { KodiakFarmStakedPosition, PortfolioFarmPosition } from '../../types/portfolio'
import { formatNumber } from '../../utils/formatCurrencyAmount'

type FarmBatchCallEntry = {
  contract: {
    address: Address
    abi: Abi
    functionName: string
    args?: readonly unknown[]
  }
  meta:
    | { kind: 'staking'; farmIndex: number }
    | { kind: 'wallet'; farmIndex: number }
    | { kind: 'underlyingBalances'; vaultAddress: Address }
    | { kind: 'underlyingTotalSupply'; vaultAddress: Address }
}

export function useFarmPositions(options?: { enabled?: boolean; filter?: string }) {
  const { account: userAccount } = useConnection()
  const { tokenData } = useTokenData()

  const {
    data: allFarms,
    isLoading: farmsLoading,
    error: farmsError,
  } = useQuery({
    queryKey: ['portfolioFarms'],
    queryFn: async () => {
      const response = await apiClient.get<{ data: APIFarm[] }>(API_ENDPOINTS.farms, {
        provider: 'kodiak',
        limit: 100,
        orderBy: 'tvl',
        orderDirection: 'desc',
      })
      // Transform API farms to include rewardTokensWithRates
      return response.data.map(transformAPIFarm)
    },
    ...PORTFOLIO_REFETCH_POLICY,
    enabled: options?.enabled !== false,
  })

  // Enrich farms with token data
  const enrichedFarms = useMemo(() => {
    if (!allFarms || !tokenData) {
      return []
    }

    return allFarms.map((farm) => ({
      ...farm,
      stakingToken: {
        ...enrichTokenWithData(
          {
            id: farm.stakingToken.id,
            address: farm.stakingToken.id,
            symbol: farm.stakingToken.symbol,
            name: farm.stakingToken.name,
            decimals: farm.stakingToken.decimals,
            totalSupply: farm.stakingToken.totalSupply.toString(),
          },
          tokenData
        ),
        price: farm.stakingToken.price,
      },
    }))
  }, [allFarms, tokenData])

  const kodiakFarms = useMemo(() => enrichedFarms.filter((farm) => farm.provider === 'kodiak'), [enrichedFarms])

  const vaultAddresses = useMemo(() => {
    return kodiakFarms
      .map((farm) => farm.vault?.id)
      .filter((vaultId): vaultId is string => Boolean(vaultId))
      .map((vaultId) => getAddress(vaultId))
  }, [kodiakFarms])

  const onchainBatchCalls = useMemo<FarmBatchCallEntry[]>(() => {
    if (!userAccount || kodiakFarms.length === 0) return []

    const calls: FarmBatchCallEntry[] = []

    kodiakFarms.forEach((farm, farmIndex) => {
      calls.push({
        contract: {
          address: getAddress(farm.id),
          abi: KodiakFarmAbi as Abi,
          functionName: 'lockedStakesOf',
          args: [userAccount as Address],
        },
        meta: { kind: 'staking', farmIndex },
      })

      calls.push({
        contract: {
          address: getAddress(farm.stakingToken.id),
          abi: ERC20_ABI as Abi,
          functionName: 'balanceOf',
          args: [userAccount as Address],
        },
        meta: { kind: 'wallet', farmIndex },
      })
    })

    vaultAddresses.forEach((vaultAddress) => {
      calls.push({
        contract: {
          address: vaultAddress,
          abi: ISLAND_ABI as Abi,
          functionName: 'getUnderlyingBalances',
        },
        meta: { kind: 'underlyingBalances', vaultAddress },
      })

      calls.push({
        contract: {
          address: vaultAddress,
          abi: ISLAND_ABI as Abi,
          functionName: 'totalSupply',
        },
        meta: { kind: 'underlyingTotalSupply', vaultAddress },
      })
    })

    return calls
  }, [kodiakFarms, userAccount, vaultAddresses])

  const {
    data: onchainBatchResults,
    isLoading: isLoadingOnchainData,
    error: onchainDataError,
  } = useReadContractMultiple({
    contracts: onchainBatchCalls.map((entry) => entry.contract),
    allowFailure: true,
    query: {
      ...PORTFOLIO_REFETCH_POLICY,
      enabled: Boolean(userAccount) && onchainBatchCalls.length > 0 && options?.enabled !== false,
    },
  })

  const { kodiakStakingByFarmIndex, walletBalanceByFarmIndex, underlyingDataByVault } = useMemo(() => {
    const stakingByFarmIndex: Record<number, KodiakFarmStakedPosition[]> = {}
    const walletByFarmIndex: Record<number, bigint> = {}
    const underlyingTokenBalancesByVault: Record<Address, readonly [bigint, bigint]> = {} as Record<
      Address,
      readonly [bigint, bigint]
    >
    const totalSupplyByVault: Record<Address, bigint> = {} as Record<Address, bigint>

    if (onchainBatchResults) {
      onchainBatchCalls.forEach((entry, index) => {
        const result = onchainBatchResults[index]
        if (!result || result.status !== 'success') return

        if (entry.meta.kind === 'staking' && Array.isArray(result.result)) {
          stakingByFarmIndex[entry.meta.farmIndex] = result.result as KodiakFarmStakedPosition[]
          return
        }

        if (entry.meta.kind === 'wallet' && typeof result.result === 'bigint') {
          walletByFarmIndex[entry.meta.farmIndex] = result.result
          return
        }

        if (entry.meta.kind === 'underlyingBalances' && Array.isArray(result.result) && result.result.length === 2) {
          underlyingTokenBalancesByVault[entry.meta.vaultAddress] = result.result as readonly [bigint, bigint]
          return
        }

        if (entry.meta.kind === 'underlyingTotalSupply' && typeof result.result === 'bigint') {
          totalSupplyByVault[entry.meta.vaultAddress] = result.result
        }
      })
    }

    const parsedUnderlyingDataByVault = vaultAddresses.reduce((acc, address) => {
      const underlyingBalances = underlyingTokenBalancesByVault[address]
      const totalSupply = totalSupplyByVault[address]
      if (underlyingBalances && totalSupply && totalSupply > 0n) {
        const [token0Amount, token1Amount] = underlyingBalances
        acc[address] = {
          token0Amount: token0Amount.toString(),
          token1Amount: token1Amount.toString(),
          totalSupply: totalSupply.toString(),
        }
      }
      return acc
    }, {} as Record<Address, { token0Amount: string; token1Amount: string; totalSupply: string }>)

    return {
      kodiakStakingByFarmIndex: stakingByFarmIndex,
      walletBalanceByFarmIndex: walletByFarmIndex,
      underlyingDataByVault: parsedUnderlyingDataByVault,
    }
  }, [onchainBatchCalls, onchainBatchResults, vaultAddresses])

  const allPositions: PortfolioFarmPosition[] = useMemo(() => {
    if (!enrichedFarms.length || !userAccount) {
      return []
    }

    const positions: PortfolioFarmPosition[] = []

    // Process Kodiak farms - check on-chain balances
    if (kodiakFarms.length > 0) {
      kodiakFarms.forEach((farm, index) => {
        const farmPositions = kodiakStakingByFarmIndex[index] || []
        if (farmPositions.length > 0) {
          const totalLiquidity = farmPositions.reduce((sum, position) => sum + position.liquidity, BigInt(0))

          if (totalLiquidity > BigInt(0)) {
            const formattedStakedAmount = formatUnits(totalLiquidity, farm.stakingToken.decimals)
            const stakedAmountUSD = Number(formattedStakedAmount) * (farm.stakingToken.price || 0)
            const isSingleFarm = farm.vault === null

            const walletBalance = walletBalanceByFarmIndex[index] || 0n
            const formattedWalletBalance = formatUnits(walletBalance, farm.stakingToken.decimals)
            const walletBalanceUSD = Number(formattedWalletBalance) * (farm.stakingToken.price || 0)

            let underlyingBalances = undefined
            if (!isSingleFarm && farm.vault) {
              const vaultAddress = getAddress(farm.vault.id)
              const underlyingData = underlyingDataByVault[vaultAddress]
              if (underlyingData && underlyingData.totalSupply !== '0') {
                const totalSupplyBN = BigInt(underlyingData.totalSupply)
                const totalToken0BN = BigInt(underlyingData.token0Amount)
                const totalToken1BN = BigInt(underlyingData.token1Amount)

                // Calculate user's underlying based on TOTAL LP (staked + wallet balance)
                const totalUserLP = totalLiquidity + walletBalance
                const userToken0 = (totalUserLP * totalToken0BN) / totalSupplyBN
                const userToken1 = (totalUserLP * totalToken1BN) / totalSupplyBN

                const token0Decimals = farm.vault.token0?.decimals || 18
                const token1Decimals = farm.vault.token1?.decimals || 18

                const token0Formatted = formatUnits(userToken0, token0Decimals)
                const token1Formatted = formatUnits(userToken1, token1Decimals)

                underlyingBalances = {
                  token0AmountFormatted: formatNumber(parseFloat(token0Formatted)) || '0',
                  token1AmountFormatted: formatNumber(parseFloat(token1Formatted)) || '0',
                  token0Amount: userToken0.toString(),
                  token1Amount: userToken1.toString(),
                }
              }
            }

            positions.push({
              farm: {
                ...farm,
                provider: farm.provider as 'kodiak' | 'bgt',
                rewardTokens: farm.rewardTokensWithRates.map((t) => ({
                  id: t.id,
                  address: t.address,
                  symbol: t.symbol,
                  name: t.name,
                  decimals: t.decimals,
                })),
                vault: farm.vault,
              },
              vault: farm.vault,
              stakedAmount: totalLiquidity.toString(),
              formattedStakedAmount,
              stakedAmountUSD,
              walletBalance: walletBalance.toString(),
              formattedWalletBalance,
              walletBalanceUSD,
              underlyingBalances,
              claimableRewards: [],
              claimableRewardsUSD: 0,
              apr: farm.apr,
              provider: 'kodiak',
              isSingleFarm,
            })
          }
        }
      })
    }

    return positions
  }, [
    enrichedFarms,
    kodiakStakingByFarmIndex,
    walletBalanceByFarmIndex,
    underlyingDataByVault,
    kodiakFarms,
    userAccount,
  ])

  const filteredPositions = useMemo(() => {
    if (!allPositions.length) return []

    let filtered = allPositions

    if (options?.filter) {
      const searchTerm = options.filter.toLowerCase()
      filtered = filtered.filter((position) => {
        const stakingTokenSymbol = position.farm.stakingToken.symbol?.toLowerCase() || ''
        const stakingTokenName = position.farm.stakingToken.name?.toLowerCase() || ''
        return stakingTokenSymbol.includes(searchTerm) || stakingTokenName.includes(searchTerm)
      })
    }

    return filtered
      .filter((position) => getFarmPositionTotalValue(position) >= HIDE_USD_BALANCE_THRESHOLD)
      .sort((a, b) => getFarmPositionTotalValue(b) - getFarmPositionTotalValue(a))
  }, [allPositions, options?.filter])

  return {
    data: filteredPositions,
    allPositions,
    isLoading: farmsLoading || isLoadingOnchainData,
    error: farmsError || onchainDataError,
  }
}
